import React, { ReactNode, useState } from 'react';
import classNames from 'classnames';
import { EditOutlined } from '@ant-design/icons';
import {
	Typography,
	Skeleton,
} from 'antd';
import { Moment } from 'moment';
import { toMoment } from '@shared/utils/date';
import { DATE } from '@shared/utils/constants';
import { formatDate } from '@client/utils/formatDate';
import Select, { SelectProps } from '@client/components/Select';
import Button from '@client/components/Button';
import DatePicker, { DatePickerProps, DatePickerValue } from './DatePicker';
import styles from './styles/EditableText.module.css';

type EditableTextProps<Value> = {
	type?: 'text' | 'date' | 'select';
	options?: SelectProps['options'] | null;
	defaultValue?: Value | null;
	placeholder?: ReactNode;
	onChange: (value: Value) => Promise<void> | void;
	textProps?: { format?: string };
	inputProps?: DatePickerProps;
	warnAsRequired?: boolean;
	disabled?: boolean;
	renderValue?: (value: Value) => ReactNode;
};

const EditableText = <Value, >({
	type = 'text',
	options,
	defaultValue,
	placeholder,
	onChange,
	textProps = {},
	inputProps = {},
	warnAsRequired = false,
	disabled = false,
	renderValue,
}: EditableTextProps<Value>) => {
	const [editing, setEditing] = useState(false);
	const [loading, setLoading] = useState(false);

	const onInputChange = async (value: unknown) => {
		setLoading(true);
		setEditing(false);
		await onChange(value as Value);
		setLoading(false);
	};

	const getInput = () => {
		if (type === 'date') {
			return (
				<DatePicker
					value={defaultValue as unknown as DatePickerValue}
					onChange={onInputChange}
					defaultOpen
					onOpenChange={(isOpen) => {
						if (!isOpen) {
							setEditing(false);
						}
					}}
					autoFocus
					{...inputProps}
				/>
			);
		}

		// if not date then select
		return (
			<Select
				defaultValue={defaultValue as unknown as string}
				onSelect={onInputChange}
				defaultOpen
				options={options ?? undefined}
				onDropdownVisibleChange={() => setEditing(false)}
				{...(inputProps as unknown as SelectProps)}
				className={classNames(styles.stateSelect, inputProps.className)}
			/>
		);
	};

	if (loading) {
		return (
			<Skeleton.Button
				className={styles.skeleton}
				active
				shape="round"
				size="small"
			/>
		);
	}

	if (editing) {
		return (<span>{getInput()}</span>);
	}

	if (type !== 'text') {
		let text: ReactNode = defaultValue as ReactNode;

		if (defaultValue != null && typeof renderValue === 'function') {
			text = renderValue(defaultValue);
		} else if (
			type === 'date' &&
			defaultValue != null &&
			toMoment(defaultValue as unknown as Moment).isValid()
		) {
			text = formatDate(defaultValue as unknown as Moment, textProps.format || DATE);
		} else if (defaultValue == null && placeholder != null) {
			text = placeholder;
		}

		return (
			<Typography.Text className={styles.customEditField}>
				{warnAsRequired && (<p className={styles.customEditFieldRequired}>﹡</p>)}
				{text}
				{!disabled && (
					<Button
						className={styles.customEditButton}
						type="link"
						onClick={(e) => {
							e?.stopPropagation();
							setEditing(!editing);
						}}
					>
						<EditOutlined />
					</Button>
				)}
			</Typography.Text>
		);
	}

	return (
		<Typography.Text
			editable={disabled ? undefined : { onChange: (value) => onInputChange(value) }}
		>
			{defaultValue == null ? placeholder : (defaultValue as ReactNode)}
		</Typography.Text>
	);
};

export default EditableText;
